import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { OverlayPanel } from 'primeng/overlaypanel';
import { filter } from 'rxjs';
import { IFilter } from '../../interfaces/ifilter';
import { IFilterData, IFilterModel } from '../../models/filter-model';
import { FilterService, IFilterSubject } from '../../services/filter.service';

export type DateFilterMode = 'range' | 'month';

@Component({
  selector: 'split-filter-date',
  templateUrl: './split-filter-date.component.html',
  styleUrls: ['./split-filter-date.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SplitFilterDateComponent implements OnInit, OnChanges {
  @Input() field!: string;
  @Input() label!: string;
  @Input() key!: string;
  @Input() groupId!: string;
  @Input() initialMode: DateFilterMode = 'range';
  @Input() initialDate: Date | null = null;
  @Input() showSelectedLabel = true;
  @Input() emitInitialEvent = true;

  @Output() onFilterEvent = new EventEmitter<IFilterModel>();

  @ViewChild('op') overlayPanel!: OverlayPanel;

  protected mode: DateFilterMode = 'range';
  protected rangeValue: Date[] = [];
  protected monthValue: Date | null = null;
  protected isActiveOverlay = false;
  protected filterModel!: IFilterModel;

  protected readonly modeOptions = [
    { label: 'Período', value: 'range' },
    { label: 'Mês', value: 'month' }
  ];

  constructor(
    private readonly filterService: FilterService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.mode = this.initialMode;

    if (this.initialDate) {
      if (this.initialMode === 'month') {
        this.monthValue = this.initialDate;
      } else {
        this.rangeValue = [this.initialDate, this.initialDate];
      }
      this.emitInitialValue();
    }

    this.filterService.filterSubject.pipe(
      filter((event: IFilterSubject) => event.action === 'CLEAR'),
      filter((event: IFilterSubject) => event.id === this.groupId)
    ).subscribe(() => {
      this.resetValue();
      this.cdr.markForCheck();
    });

    this.filterService.filterSubject.pipe(
      filter((event: IFilterSubject) => event.action === 'DELETE'),
      filter((event: IFilterSubject) => event.id === this.groupId),
      filter((event: IFilterSubject) => this.isSameDateFilter(event.data))
    ).subscribe(() => {
      this.resetValue();
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['key'] || changes['groupId']) {
      this.setFilterModelType();
    }

    if (changes['initialDate'] && !changes['initialDate'].firstChange) {
      this.applyInitialDate();
      this.emitInitialValue();
      this.cdr.markForCheck();
    }
  }

  get hasValue(): boolean {
    if (this.mode === 'range') {
      return (
        this.rangeValue.length === 2 &&
        this.rangeValue[0] instanceof Date &&
        this.rangeValue[1] instanceof Date
      );
    }
    return this.monthValue instanceof Date;
  }

  get displayLabel(): string {
    if (!this.hasValue || !this.showSelectedLabel) return this.label;
    if (this.mode === 'range' && this.rangeValue.length === 2) {
      return `${this.formatDateDisplay(this.rangeValue[0])} - ${this.formatDateDisplay(this.rangeValue[1])}`;
    }
    if (this.mode === 'month' && this.monthValue) {
      return this.formatMonthDisplay(this.monthValue);
    }
    return this.label;
  }

  showOverlayPanel(event: Event): void {
    this.isActiveOverlay = true;
    this.overlayPanel.show(event);
  }

  hideOverlayPanel(): void {
    this.isActiveOverlay = false;
  }

  onModeChange(): void {
    if (this.mode === 'month') {
      this.monthValue = this.getMonthFromRange();
    } else {
      this.rangeValue = this.getRangeFromMonth();
    }

    this.setFilterModelType();
  }

  onCancel(): void {
    this.rangeValue = [];
    this.monthValue = null;
    this.emit();
    this.overlayPanel.hide();
  }

  onApply(): void {
    this.emit();
    this.overlayPanel.hide();
  }

  private emit(): void {
    const fm = this.buildFilterModel();
    this.filterService.send(fm);
    this.onFilterEvent.emit(fm);
  }

  private emitInitialValue(): void {
    if (!this.emitInitialEvent) return;
    this.emit();
  }

  private setFilterModelType(): void {
    this.filterModel = {
      ...(this.filterModel ?? {}),
      id: this.groupId,
      type: this.mode === 'month' ? 'MONTH' : 'DATE',
      key: this.key,
      data: this.filterModel?.data ?? [],
      filter: this.filterModel?.filter ?? []
    };
  }

  private applyInitialDate(): void {
    if (!this.initialDate) {
      this.rangeValue = [];
      this.monthValue = null;
      return;
    }

    if (this.initialMode === 'month') {
      this.mode = 'month';
      this.monthValue = this.initialDate;
      this.rangeValue = [];
    } else {
      this.mode = 'range';
      this.rangeValue = [this.initialDate, this.initialDate];
      this.monthValue = null;
    }

    this.setFilterModelType();
  }

  private resetValue(): void {
    this.mode = this.initialMode;
    this.rangeValue = [];
    this.monthValue = null;
    this.setFilterModelType();
  }

  private isSameDateFilter(model: IFilterModel | null): boolean {
    if (!model) return false;
    return model.key === this.key || ['date', 'month'].includes(model.key) || ['DATE', 'MONTH'].includes(model.type);
  }

  private getMonthFromRange(): Date | null {
    if (this.monthValue) return this.monthValue;
    if (this.rangeValue[0] instanceof Date) return this.rangeValue[0];
    return null;
  }

  private getRangeFromMonth(): Date[] {
    if (this.rangeValue.length === 2 && this.rangeValue[0] instanceof Date && this.rangeValue[1] instanceof Date) {
      return this.rangeValue;
    }

    if (!this.monthValue) return [];

    const year = this.monthValue.getFullYear();
    const month = this.monthValue.getMonth();
    return [
      new Date(year, month, 1),
      new Date(year, month + 1, 0)
    ];
  }

  protected buildFilterModel(): IFilterModel {
    const type = this.mode === 'month' ? 'MONTH' : 'DATE';
    let filterItems: IFilter[] = [];
    let data: IFilterData[] = [];

    if (this.mode === 'range' && this.hasValue) {
      const start = this.toLocalDateString(this.rangeValue[0]);
      const end = this.toLocalDateString(this.rangeValue[1]);
      data = [
        { id: 'after', name: this.rangeValue[0] },
        { id: 'before', name: this.rangeValue[1] }
      ];
      filterItems = [
        { key: this.key, fields: ['after'], property: this.field, value: start },
        { key: this.key, fields: ['before'], property: this.field, value: end }
      ];
    } else if (this.mode === 'month' && this.monthValue) {
      const year = this.monthValue.getFullYear();
      const month = this.monthValue.getMonth();
      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 0);
      data = [
        { id: 'after', name: startDate },
        { id: 'before', name: endDate }
      ];
      filterItems = [
        { key: this.key, fields: ['after'], property: this.field, value: this.toLocalDateString(startDate) },
        { key: this.key, fields: ['before'], property: this.field, value: this.toLocalDateString(endDate) }
      ];
    }

    return { ...this.filterModel, type, data, filter: filterItems };
  }

  private toLocalDateString(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private formatDateDisplay(date: Date): string {
    return this.toLocalDateString(date);
  }

  private formatMonthDisplay(date: Date): string {
    const year = date.getFullYear();
    const month = date.getMonth();
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);
    return `${this.toLocalDateString(startDate)} - ${this.toLocalDateString(endDate)}`;
  }
}
